export type Workspace = 'anchor' | 'supplier' | 'ops'
export const WORKSPACES: readonly Workspace[] = ['anchor', 'supplier', 'ops']

/** The UI checks permission keys, never role names (spec §5). */
export const P = {
  ANCHOR_ONBOARDING_EDIT: 'anchor.onboarding.edit',
  ANCHOR_SUPPLIER_NOMINATE: 'anchor.supplier.nominate',
  ANCHOR_SUPPLIERS_CREATE: 'anchor.suppliers.create',
  ANCHOR_SUPPLIERS_INVITE: 'anchor.suppliers.invite',
  ANCHOR_INTEGRATION_MANAGE: 'anchor.integration.manage',
  ANCHOR_USERS_MANAGE: 'anchor.users.manage',
  SUPPLIER_REGISTRATION_EDIT: 'supplier.registration.edit',
  SUPPLIER_FINANCING_SUBMIT: 'supplier.financing.submit',
  SUPPLIER_ACCOUNT_MANAGE: 'supplier.account.manage',
  OPS_MESSAGES_REPLAY: 'ops.messages.replay',
  OPS_ELIGIBILITY_MANAGE: 'ops.eligibility.manage',
  OPS_ADMIN: 'ops.admin',
  OPS_ANCHORS_MANAGE: 'ops.anchors.manage',
  OPS_ANCHORS_EDIT: 'ops.anchors.edit',
  OPS_ANCHORS_ACTIVATE: 'ops.anchors.activate',
  OPS_ANCHORS_SUSPEND: 'ops.anchors.suspend',
  OPS_ANCHOR_LINK_SUSPEND: 'ops.anchorlink.suspend',
  OPS_ANCHOR_LINK_REACTIVATE: 'ops.anchorlink.reactivate',
  OPS_SUPPLIERS_CREATE: 'ops.suppliers.create',
  OPS_SUPPLIERS_EDIT: 'ops.suppliers.edit',
  OPS_SUPPLIERS_ACTIVATE: 'ops.suppliers.activate',
  OPS_SUPPLIERS_SUSPEND: 'ops.suppliers.suspend',
  OPS_SUPPLIERS_CLOSE: 'ops.suppliers.close',
  OPS_FI_STAFF_LINK: 'ops.fistaff.link',
  OPS_PROGRAMME_ANCHOR_SETUP: 'ops.programmes.anchorsetup',
  OPS_ANCHORS_CLOSE: 'ops.anchors.close',
  OPS_ANCHORS_SYNC: 'ops.anchors.sync',
  OPS_ANCHOR_CONTACTS_CREATE: 'ops.anchorcontacts.create',
  OPS_ANCHOR_CONTACTS_EDIT: 'ops.anchorcontacts.edit',
  OPS_ANCHOR_USERS_LINK: 'ops.anchorusers.link',
  OPS_RELATIONSHIPS_CREATE: 'ops.relationships.create',
  OPS_RELATIONSHIPS_CATEGORIZE: 'ops.relationships.categorize',
  OPS_RELATIONSHIPS_SUSPEND: 'ops.relationships.suspend',
  OPS_RELATIONSHIPS_TERMINATE: 'ops.relationships.terminate',
  OPS_NOMINATIONS_CREATE: 'ops.nominations.create',
  OPS_NOMINATIONS_WITHDRAW: 'ops.nominations.withdraw',
  OPS_NOMINATIONS_SUSPEND: 'ops.nominations.suspend',
  OPS_ANCHOR_POLICIES_EDIT: 'ops.anchorpolicies.edit',
  OPS_ADVICES_SUBMIT: 'ops.advices.submit',
} as const
export type Permission = (typeof P)[keyof typeof P]

export function isPermission(value: string): value is Permission {
  return (Object.values(P) as string[]).includes(value)
}

/**
 * Backend permission codes (RESOURCE:ACTION, from POST /auth/login) that grant each UI key;
 * holding any one of them grants the key. Assumed mapping: confirm with the backend team.
 */
export const BACKEND_GRANTS: Record<Permission, readonly string[]> = {
  [P.ANCHOR_ONBOARDING_EDIT]: ['ANCHOR_PROGRAMME:REGISTER', 'ANCHOR_OP_CONFIG:UPDATE'],
  [P.ANCHOR_SUPPLIER_NOMINATE]: ['NOMINATION:CREATE'],
  // an anchor user adding a supplier profile (DRAFT); assumed: confirm anchor actors receive it
  [P.ANCHOR_SUPPLIERS_CREATE]: ['SUPPLIER:CREATE'],
  // invitation = relationship + nomination in one call; assumed code, NOMINATION:CREATE as fallback
  [P.ANCHOR_SUPPLIERS_INVITE]: ['SUPPLIER_INVITATION:CREATE', 'NOMINATION:CREATE'],
  [P.ANCHOR_INTEGRATION_MANAGE]: ['ANCHOR_OP_CONFIG:UPDATE'],
  [P.ANCHOR_USERS_MANAGE]: ['ANCHOR_USER_LINK:CREATE'],
  [P.SUPPLIER_REGISTRATION_EDIT]: ['SUPPLIER_REGISTRATION:UPDATE'],
  [P.SUPPLIER_FINANCING_SUBMIT]: ['FINANCING_REQUEST:CREATE'],
  [P.SUPPLIER_ACCOUNT_MANAGE]: ['SUPPLIER:UPDATE'],
  [P.OPS_MESSAGES_REPLAY]: ['LOS_INTEGRATION:PUBLISH'],
  [P.OPS_ELIGIBILITY_MANAGE]: ['ELIGIBILITY:ASSESS'],
  [P.OPS_ADMIN]: ['USER:MANAGE', 'ROLE:MANAGE'],
  [P.OPS_ANCHORS_MANAGE]: ['ANCHOR:CREATE'],
  [P.OPS_ANCHORS_EDIT]: ['ANCHOR:UPDATE'],
  // no dedicated code for DRAFT → UNDER_SETUP; it is gated with activation (assumed)
  [P.OPS_ANCHORS_ACTIVATE]: ['ANCHOR:ACTIVATE'],
  [P.OPS_ANCHORS_SUSPEND]: ['ANCHOR:SUSPEND'],
  [P.OPS_ANCHOR_LINK_SUSPEND]: ['ANCHOR_FINANCING_LINK:SUSPEND'],
  [P.OPS_ANCHOR_LINK_REACTIVATE]: ['ANCHOR_FINANCING_LINK:REACTIVATE'],
  [P.OPS_SUPPLIERS_CREATE]: ['SUPPLIER:CREATE'],
  // no dedicated code for DRAFT → SUBMITTED; whoever may edit the draft may submit it (assumed)
  [P.OPS_SUPPLIERS_EDIT]: ['SUPPLIER:UPDATE'],
  [P.OPS_SUPPLIERS_ACTIVATE]: ['SUPPLIER:ACTIVATE'],
  [P.OPS_SUPPLIERS_SUSPEND]: ['SUPPLIER:SUSPEND'],
  [P.OPS_SUPPLIERS_CLOSE]: ['SUPPLIER:CLOSE'],
  [P.OPS_FI_STAFF_LINK]: ['FI_USER_LINK:CREATE'],
  // anchor registration and the readiness check; no separate code for the check (assumed)
  [P.OPS_PROGRAMME_ANCHOR_SETUP]: ['ANCHOR_PROGRAMME:REGISTER'],
  [P.OPS_ANCHORS_CLOSE]: ['ANCHOR:CLOSE'],
  // no dedicated code for "push to LOS again"; gated with editing the anchor (assumed)
  [P.OPS_ANCHORS_SYNC]: ['ANCHOR:UPDATE'],
  [P.OPS_ANCHOR_CONTACTS_CREATE]: ['ANCHOR_CONTACT:CREATE'],
  // editing and (soft) deactivating a contact
  [P.OPS_ANCHOR_CONTACTS_EDIT]: ['ANCHOR_CONTACT:UPDATE'],
  [P.OPS_ANCHOR_USERS_LINK]: ['ANCHOR_USER_LINK:CREATE'],
  [P.OPS_RELATIONSHIPS_CREATE]: ['SUPPLIER_RELATIONSHIP:CREATE'],
  [P.OPS_RELATIONSHIPS_CATEGORIZE]: ['SUPPLIER_RELATIONSHIP:CATEGORIZE'],
  // suspend and reactivate (there is no separate reactivate code)
  [P.OPS_RELATIONSHIPS_SUSPEND]: ['SUPPLIER_RELATIONSHIP:SUSPEND'],
  [P.OPS_RELATIONSHIPS_TERMINATE]: ['SUPPLIER_RELATIONSHIP:TERMINATE'],
  [P.OPS_NOMINATIONS_CREATE]: ['NOMINATION:CREATE'],
  [P.OPS_NOMINATIONS_WITHDRAW]: ['NOMINATION:WITHDRAW'],
  // suspend and reactivate (there is no separate reactivate code)
  [P.OPS_NOMINATIONS_SUSPEND]: ['NOMINATION:SUSPEND'],
  // invoice-verification and payment-confirmation policies (anchor operational configuration)
  [P.OPS_ANCHOR_POLICIES_EDIT]: ['ANCHOR_OP_CONFIG:CREATE', 'ANCHOR_OP_CONFIG:UPDATE'],
  // admin intake of an anchor's invoice advice (manual entry or a parsed file)
  [P.OPS_ADVICES_SUBMIT]: ['INVOICE_ADVICE:SUBMIT'],
}

export function fromBackendPermissions(codes: readonly string[]): Permission[] {
  const held = new Set(codes)
  return (Object.keys(BACKEND_GRANTS) as Permission[]).filter((p) =>
    BACKEND_GRANTS[p].some((code) => held.has(code)),
  )
}
